import { useState } from 'react';
import { PaymentResponseStatus, PaymentStatusGroup } from 'core/enums/payment-status';
import { useAppContext, usePaymentSession } from 'core/hooks';
import { MakePaymentPayloadDTO } from 'core/stores/payment/interfaces/make-payment-payload.dto';
import { PaymentService } from 'core/stores/payment/interfaces/payment-service';

//Intentionally not using PaymentPollingStore. Don't want to use PaymentLoader & we just need something simple

const getPaymentStatus = (statusString: string): PaymentResponseStatus => {
  const status = (statusString ?? '').toUpperCase() as PaymentResponseStatus;

  const mapping = new Map<PaymentResponseStatus[], PaymentResponseStatus>([
    [PaymentStatusGroup.pending, PaymentResponseStatus.PENDING],
    [PaymentStatusGroup.failed, PaymentResponseStatus.FAILURE],
    [PaymentStatusGroup.success, PaymentResponseStatus.SUCCESS],
    [PaymentStatusGroup.notProcessing, PaymentResponseStatus.CREATED],
  ]);

  for (const [key, value] of mapping.entries()) {
    if (key.includes(status)) {
      return value;
    }
  }

  return PaymentResponseStatus.CREATED;
}

type UsePaymentStatusLogicProps = {
  paymentService: PaymentService<MakePaymentPayloadDTO>;
  onSuccess: () => void;
  onFailure?: () => void;
  onMaxAttemptsReached: () => void;
}

const usePaymentStatusLogic = (props: UsePaymentStatusLogicProps) => {
  const { paymentService, onSuccess } = props;

  const paymentSession = usePaymentSession();
  const transactionId = paymentSession?.transactionId || '';
  const { customerNumber, countryName } = useAppContext();

  const pollInterval = 1000;
  const maxAttempts = 10;

  const [attempt, setAttempt] = useState(0);

  const incrementAttempt = () => setAttempt(attempt + 1);

  const reset = () => setAttempt(0);

  const checkPaymentStatus = async () => {
    if (attempt == maxAttempts) {
      props.onMaxAttemptsReached();
      return;
    }

    try {
      const response = await paymentService.PaymentStatus(countryName, customerNumber, transactionId);

      const status = getPaymentStatus(response.status);

      if (status === PaymentResponseStatus.SUCCESS) {
        reset();
        onSuccess();
        return;
      }

      if (status === PaymentResponseStatus.FAILURE) {
        if (props?.onFailure) {
          props.onFailure();
        }

        return;
      }

      incrementAttempt();

      setTimeout(checkPaymentStatus, pollInterval);
    } catch (error) {
      console.error(error);

      if (props?.onFailure) {
        props.onFailure();
      }

      return;
    }
  }

  return {
    checkPaymentStatus,
  };
}

export default usePaymentStatusLogic;